import { useColorMode, useEventListener, useFetch } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import { UserRole } from '@/enums/operator'
import { AUTH_BASE_PATH } from '@/libs/auth'
import i18n from '@/libs/i18n'
import type { IMe } from '@/types/Operator'

export const useAppStore = defineStore('app', () => {
    // English only for now (libs/i18n). The state and changeLocale stay so a
    // second bundle is a file and a switcher rather than a refactor; nothing
    // in the UI sets it today, and a stale 'sl' left in a browser's storage
    // from an earlier build must not resolve to a bundle that is not shipped.
    const locale       = ref('en')
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    // The signed-in person, from GET /auth/me (README decision 328). Asked
    // once per page load — every sign-in is a full page load — by the router's
    // first navigation.
    const userData     = ref<IMe>()
    const sessionAsked = ref(false)

    // Anything but a confirmed admin is shown the operator's portal: the
    // server enforces the role either way, so a failed lookup costs menu items,
    // never access (README decision 321).
    const role     = computed(() => userData.value?.extra?.role ?? '')
    const isAdmin  = computed(() => role.value === UserRole.Admin)
    const fullName = computed(() => userData.value?.full_name ?? '')

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() —
    // never a second source of truth. Two modes only, 'light' | 'dark' — there is no System
    // option in the UI (vue-spec theme.md). Without `emitAuto` the ref always reads the
    // RESOLVED mode, so a first visit, with nothing stored yet, follows the OS.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    // Keep the breakpoint LIVE: read once, `isMobile` would freeze the form factor — and with
    // it the mobile drawer — until a reload. Bound to the store's effect scope.
    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const isLoggedIn     = () => !!userData.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024

    // Not through the fetch store: its 401 rule would answer the very call
    // that finds the session gone.
    const loadSession = async (force = false): Promise<void> => {
        if (sessionAsked.value && !force) return

        sessionAsked.value = true

        const { statusCode, data } = await useFetch(`${AUTH_BASE_PATH}/me`, {
            credentials: 'same-origin',
            headers:     { Accept: 'application/json' },
        }).get().json<IMe>()

        userData.value = statusCode.value === 200 && data.value ? data.value : undefined
    }

    // The session ends in this portal only (README decision 328).
    const signOut = async (): Promise<void> => {
        await useFetch(`${AUTH_BASE_PATH}/logout`, { method: 'POST', credentials: 'same-origin' })

        userData.value = undefined
    }

    const clearSession = () => { userData.value = undefined }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    const changeLocale = (newLocale: string) => {
        locale.value = newLocale
        localStorage.setItem('locale', newLocale)
        ;(i18n.global.locale as unknown as { value: string }).value = newLocale
    }

    // Anything a previous build persisted is ignored: the bundle it names may
    // not be in this one.
    localStorage.removeItem('locale')
    // The token an earlier build kept; the session is a cookie now.
    localStorage.removeItem('JWT')

    return {
        locale, userData, role, isAdmin, fullName, loaderActive, colorMode, windowWidth,
        isLoggedIn, isLoaderActive, isMobile,
        loadSession, signOut, clearSession, startLoading, stopLoading, changeLocale,
    }
})
